/**
 * Page state, in the URL.
 *
 * Same reasoning as the global time range: a page number that lives only in
 * component state is lost on every navigation and cannot be shared. "Look at
 * row 340 of the product table" should be a link, not a set of instructions.
 *
 * `prefix` lets one screen carry more than one independent pager (the brand
 * page has a product table under a set of summary cards) without the two
 * fighting over the same query param.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const DEFAULT_PAGE_SIZE = 50;

export function usePageParams(prefix = '') {
  const [params, setParams] = useSearchParams();
  const pageKey = prefix ? `${prefix}_page` : 'page';
  const sizeKey = prefix ? `${prefix}_size` : 'size';

  const page = Math.max(1, Number(params.get(pageKey)) || 1);
  const pageSize = Math.max(1, Number(params.get(sizeKey)) || DEFAULT_PAGE_SIZE);

  const patch = useCallback((updates) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value == null) next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  const setPage = useCallback((next) => {
    // Page 1 is the default, so it stays out of the URL — a link to the first
    // page of anything should not carry ?page=1.
    patch({ [pageKey]: next > 1 ? next : null });
  }, [patch, pageKey]);

  const setPageSize = useCallback((size) => {
    // Changing rows-per-page always returns to the first page: staying on
    // page 7 while the page size triples lands you somewhere arbitrary, which
    // reads as data having vanished.
    patch({
      [sizeKey]: size !== DEFAULT_PAGE_SIZE ? size : null,
      [pageKey]: null,
    });
  }, [patch, sizeKey, pageKey]);

  /** Call when a filter or sort changes — the row you were on no longer exists. */
  const resetPage = useCallback(() => patch({ [pageKey]: null }), [patch, pageKey]);

  return useMemo(
    () => ({ page, pageSize, setPage, setPageSize, resetPage }),
    [page, pageSize, setPage, setPageSize, resetPage],
  );
}

/**
 * Reads the pagination envelope the backend returns, tolerating a plain
 * `{items: []}` from an endpoint that has not been paginated.
 */
export function pageMeta(data) {
  if (!data) return null;
  if (data.total == null) return null;
  return {
    page: data.page,
    page_size: data.page_size,
    total: data.total,
    total_pages: data.total_pages,
    has_more: data.has_more,
  };
}
